import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export type ScenarioKind = 'restaurant' | 'order-restaurant' | 'order-item';

export interface ArmedScenario {
  kind: ScenarioKind;
  /** Slug the server compares against */
  target: string;
  /** Display name, for showing to humans */
  targetName: string;
  source: 'env' | 'manual' | 'rotation';
}

export interface DemoFailureState {
  source: 'env' | 'manual' | 'rotation' | 'none';
  week: string;
  armed: { kind: ScenarioKind; target: string; targetName: string } | null;
}

export interface ArmedInvestigation {
  slug: string;
  /** For I2: the slow query's length in seconds. Absent for other arms. */
  seconds?: number;
  /** For I7: the lock helper's backend PID and when its window ends. */
  lockPid?: number | null;
  windowEndsAt?: string | null;
  lockError?: string;
  /** For I8: seed progress and whether the index fix has been applied. */
  seedStatus?: 'idle' | 'seeding' | 'ready' | 'error';
  seededCustomers?: number;
  indexApplied?: boolean;
  source: 'env' | 'manual';
}

export interface DemoInvestigationState {
  source: 'env' | 'manual' | 'none';
  armed: { slug: string; seconds?: number } | null;
}

const NR_BASE_URL = 'https://one.newrelic.com';
const CALLOUTS_STORAGE_KEY = 'orderup-nr-callouts';
const CALLOUTS_SHOWN_BY_DEFAULT = false;

@Injectable({ providedIn: 'root' })
export class NrConfigService {
  // Stays null until the server answers, or if NEW_RELIC_ACCOUNT_ID isn't set
  readonly accountId = signal<string | null>(null);

  readonly browserEntityGuid = signal<string | null>(null);
  readonly showCallouts = signal(this.loadShowCallouts());

  /** The demo failure scenario the server currently has armed, if any. */
  readonly armedScenario = signal<ArmedScenario | null>(null);
  readonly demoState = signal<DemoFailureState | null>(null);
  readonly armedInvestigation = signal<ArmedInvestigation | null>(null);
  readonly investigationState = signal<DemoInvestigationState | null>(null);

  constructor(private http: HttpClient) {
    http.get<{ accountId: string | null; browserEntityGuid: string | null; armedScenario: ArmedScenario | null; armedInvestigation: ArmedInvestigation | null }>('/api/nr-config')
      .subscribe({
        next: config => {
          this.accountId.set(config.accountId);
          this.browserEntityGuid.set(config.browserEntityGuid);
          this.armedScenario.set(config.armedScenario);
          this.armedInvestigation.set(config.armedInvestigation);
        },
        error: () => {}
      });
  }

  /** Plain-language description of the armed scenario, for banners/panels. */
  static describeScenario(s: ArmedScenario): string {
    switch (s.kind) {
      case 'restaurant': return `The page for “${s.targetName}” fails to load.`;
      case 'order-restaurant': return `Orders placed with “${s.targetName}” fail.`;
      case 'order-item': return `Orders containing “${s.targetName}” fail.`;
    }
  }

  refreshDemoState(): void {
    this.http.get<DemoFailureState>('/api/demo/failures').subscribe({
      next: state => this.applyDemoState(state),
      error: () => {}
    });
  }

  /** Arm one scenario manually (instructor override). */
  armScenario(kind: ScenarioKind, target: string): void {
    this.http.post<DemoFailureState>('/api/demo/failures', { kind, target }).subscribe({
      next: state => this.applyDemoState(state),
      error: () => {}
    });
  }

  /** Arm nothing at all (instructor override), until cleared. */
  disarmScenario(): void {
    this.http.post<DemoFailureState>('/api/demo/failures', { kind: 'off' }).subscribe({
      next: state => this.applyDemoState(state),
      error: () => {}
    });
  }

  /** Drop the instructor override; back to env / weekly rotation. */
  clearScenarioOverride(): void {
    this.http.delete<DemoFailureState>('/api/demo/failures').subscribe({
      next: state => this.applyDemoState(state),
      error: () => {}
    });
  }

  /** Investigation titles by slug, for banners and panels. */
  static investigationTitle(slug: string): string {
    switch (slug) {
      case 'its-not-checkout': return 'It’s not checkout';
      case 'the-502-didnt-come-from-the-app': return 'The 502 didn’t come from the app';
      case 'one-connection-for-the-whole-restaurant': return 'One connection for the whole restaurant';
      case 'the-table-is-locked': return 'The table is locked';
      case 'the-slow-lookup': return 'The slow lookup';
      default: return slug;
    }
  }

  /** Plain-language description of the armed investigation, for the banner. */
  static describeInvestigation(a: ArmedInvestigation): string {
    switch (a.slug) {
      case 'its-not-checkout':
        return `“It’s not checkout” is live: every order’s database work includes a ${a.seconds}s slow query.`;
      case 'the-502-didnt-come-from-the-app':
        return '“The 502 didn’t come from the app” is live: about half of restaurant and menu requests are dying at the proxy.';
      case 'one-connection-for-the-whole-restaurant':
        return '“One connection for the whole restaurant” is live: a simulated visitor (SIM-BST1) is placing concurrent orders.';
      case 'the-table-is-locked':
        return a.lockPid
          ? `“The table is locked” is live: a helper transaction (backend PID ${a.lockPid}) is holding a lock on the orders table${a.windowEndsAt ? ` until ${new Date(a.windowEndsAt).toLocaleTimeString()}` : ''}. Placing an order will hang.`
          : '“The table is locked” is live: a helper transaction is holding a lock on the orders table. Placing an order will hang.';
      case 'the-slow-lookup':
        if (a.indexApplied) return '“The slow lookup” is live — and the index fix has been applied. The lookup should be fast again; checkpoint 6 verifies it.';
        if (a.seedStatus === 'seeding') return `“The slow lookup” is live: synthetic order history is still seeding (${a.seededCustomers ?? 0} customers so far). Give it a minute.`;
        return `“The slow lookup” is live: ${a.seededCustomers ?? 0} synthetic customers of order history are in the database, and the phone lookup has no index.`;
      case 'the-kitchen-never-got-the-ticket':
        return '“The kitchen never got the ticket” is live: orders have stopped advancing — the site is fine, the queue is not.';
      default:
        return `Investigation “${a.slug}” is live.`;
    }
  }

  refreshInvestigationState(): void {
    this.http.get<DemoInvestigationState>('/api/demo/investigations').subscribe({
      next: state => this.applyInvestigationState(state),
      error: () => {}
    });
  }

  /** Arm an investigation manually (instructor override). */
  armInvestigation(slug: string, seconds?: number): void {
    this.http.post<DemoInvestigationState>('/api/demo/investigations', { slug, seconds }).subscribe({
      next: state => this.applyInvestigationState(state),
      error: () => {}
    });
  }

  /** Apply I8's fix (index on customers.phone) while it is armed. */
  applyInvestigationFix(): void {
    this.http.post<DemoInvestigationState>('/api/demo/investigations', { action: 'apply-index' }).subscribe({
      next: state => this.applyInvestigationState(state),
      error: () => {}
    });
  }

  /** Arm no investigation (instructor override), until cleared. */
  disarmInvestigation(): void {
    this.http.post<DemoInvestigationState>('/api/demo/investigations', { slug: 'off' }).subscribe({
      next: state => this.applyInvestigationState(state),
      error: () => {}
    });
  }

  /** Clear the manual override, back to the server environment. */
  clearInvestigationOverride(): void {
    this.http.delete<DemoInvestigationState>('/api/demo/investigations').subscribe({
      next: state => this.applyInvestigationState(state),
      error: () => {}
    });
  }

  private applyInvestigationState(state: DemoInvestigationState): void {
    this.investigationState.set(state);
    this.armedInvestigation.set(state.armed ? { ...state.armed, source: state.source as ArmedInvestigation['source'] } : null);
  }

  private applyDemoState(state: DemoFailureState): void {
    this.demoState.set(state);
    this.armedScenario.set(state.armed ? { ...state.armed, source: state.source as ArmedScenario['source'] } : null);
  }

  private accountNoteClaimed = false;

  /**
   * The spec section 5.4 account note ("opens in the shared demo account")
   * is shown once per page, under the first callout's Find button. The
   * first callout to ask claims it; later callouts on the page get false.
   */
  claimAccountNote(): boolean {
    if (this.accountNoteClaimed) return false;
    this.accountNoteClaimed = true;
    return true;
  }

  toggleCallouts(): void {
    this.showCallouts.update(shown => !shown);
    localStorage.setItem(CALLOUTS_STORAGE_KEY, String(this.showCallouts()));
  }

  private loadShowCallouts(): boolean {
    // Instructor override (spec section 7): ?callouts=1 on a handout or QR
    // link shows the callouts for this visit without changing the visitor's
    // stored preference; ?callouts=0 forces them off the same way.
    if (typeof location !== 'undefined') {
      const param = new URLSearchParams(location.search).get('callouts');
      if (param === '1' || param === 'true') return true;
      if (param === '0' || param === 'false') return false;
    }
    const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(CALLOUTS_STORAGE_KEY);
    return stored === null ? CALLOUTS_SHOWN_BY_DEFAULT : stored === 'true';
  }

  browserErrorsUrl(): string | null {
    const guid = this.browserEntityGuid();
    return guid ? `${NR_BASE_URL}/nr1-core/errors-inbox/entity-inbox/${guid}` : null;
  }

  nrqlUrl(nrql: string): string | null {
    const accountId = Number(this.accountId());
    if (!accountId) return null;
    const pane = {
      nerdletId: 'data-exploration.query-builder',
      initialActiveInterface: 'nrqlEditor',
      initialQueries: [{ accountId, nrql }],
      initialAccountId: accountId
    };
    return `${NR_BASE_URL}/launcher/data-exploration.query-builder?pane=${btoa(JSON.stringify(pane))}`;
  }
}
